import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShoppingCart, ArrowLeft, Minus, Plus, Zap, ArrowRight, Share2, Copy, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { PRODUCT } from "@/constants/testIds";
import { toast } from "sonner";
import ProductImage from "@/components/ProductImage";
import { recordViewed } from "@/hooks/useRecentlyViewed";

function ShareRow({ productName }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `Check out ${productName} on WeAutomate`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  // navigator.share for mobile OS share sheet when available.
  const nativeShare = async () => {
    if (!navigator.share) { copy(); return; }
    try { await navigator.share({ title: productName, text, url }); }
    catch { /* user cancelled */ }
  };
  const encoded = encodeURIComponent(`${text} ${url}`);
  return (
    <div className="mt-10 pt-6 border-t border-white/5">
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-3">Share</div>
      <div className="flex flex-wrap gap-2">
        <a href={`https://wa.me/?text=${encoded}`}
           target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/10 hover:bg-white/5">
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </a>
        <a href={`https://twitter.com/intent/tweet?text=${encoded}`}
           target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/10 hover:bg-white/5">
          <Share2 className="w-3.5 h-3.5" /> X / Twitter
        </a>
        <button onClick={copy}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/10 hover:bg-white/5">
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare}
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border border-white/10 hover:bg-white/5">
            <Share2 className="w-3.5 h-3.5" /> Share...
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    // Reset scroll + local state when navigating between products, otherwise
    // clicking a related product below the fold leaves the viewport there.
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
    setP(null);
    setRelated([]);
    setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => nav("/marketplace"));
  }, [slug, nav]);

  // Load related products (same category, excluding this one) once the
  // main product is known. Kept as a separate effect so a slow /products
  // response never blocks the primary product from rendering.
  useEffect(() => {
    if (!p?.category) return;
    api.get("/products", { params: { category: p.category } })
      .then(({ data }) => setRelated(data.filter((x) => x.slug !== p.slug).slice(0, 4)))
      .catch(() => {});
  }, [p?.category, p?.slug]);

  // Push this product onto the "recently viewed" list so the Home page
  // and other consumers can show it later.
  useEffect(() => { if (p) recordViewed(p); }, [p]);

  if (!p) return (
    <div className="py-32 text-center">
      <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-accent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to marketplace
        </Link>
        <div className="mt-8 grid lg:grid-cols-2 gap-12">
          <div className="rounded-3xl overflow-hidden glass aspect-[4/3]">
            <ProductImage src={p.images?.[0]} alt={p.name} iconSize="w-16 h-16" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">{p.category} · SKU {p.sku}</div>
            <h1 data-testid={PRODUCT.title} className="font-display text-4xl lg:text-5xl mt-3 tracking-tighter">{p.name}</h1>
            <p className="mt-4 text-white/70 leading-relaxed">{p.description || p.short_desc}</p>

            <div className="mt-8 flex items-end gap-4">
              <div data-testid={PRODUCT.price} className="font-display text-4xl text-accent">₹{p.price.toLocaleString("en-IN")}</div>
              <div className="text-xs uppercase tracking-widest text-white/40 font-mono pb-2">{p.tier} price · incl. tax extra</div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center glass rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))}
                        className="w-10 h-10 flex items-center justify-center hover:text-white text-white/60">
                  <Minus className="w-4 h-4" />
                </button>
                <input data-testid={PRODUCT.qtyInput} type="number" value={qty}
                       onChange={(e) => {
                         // parseInt("") is NaN; || 1 catches it (NaN is falsy),
                         // Math.max clamps below 1, Math.floor drops decimals.
                         const n = parseInt(e.target.value, 10);
                         setQty(Math.max(1, Math.floor(Number.isFinite(n) ? n : 1)));
                       }}
                       className="w-12 text-center bg-transparent outline-none" />
                <button onClick={() => setQty(qty + 1)}
                        className="w-10 h-10 flex items-center justify-center hover:text-white text-white/60">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button data-testid={PRODUCT.addToCart}
                      onClick={() => { add(p, qty); toast.success("Added to cart"); }}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:bg-white/5">
                <ShoppingCart className="w-4 h-4" /> Add to cart
              </button>
              <button data-testid={PRODUCT.buyNow}
                      onClick={() => { add(p, qty); nav("/checkout"); }}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full gradient-bg font-medium">
                <Zap className="w-4 h-4" /> Buy now
              </button>
            </div>

            {Object.keys(p.specs || {}).length > 0 && (
              <div className="mt-10 grid grid-cols-2 gap-3">
                {Object.entries(p.specs).map(([k, v]) => (
                  <div key={k} className="glass rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{k}</div>
                    <div className="mt-1 text-sm">{String(v)}</div>
                  </div>
                ))}
              </div>
            )}

            <ShareRow productName={p.name} />
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-24 border-t border-white/5 pt-16">
            <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">More in {p.category}</div>
                <h2 className="font-display text-3xl mt-3 tracking-tighter">You might also like</h2>
              </div>
              <Link to="/marketplace" className="text-sm inline-flex items-center gap-2 text-white/60 hover:text-white">
                See the full catalog <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((r) => (
                <Link key={r.id} to={`/product/${r.slug}`}
                      className="group glass rounded-2xl overflow-hidden hover:border-white/25 transition-colors">
                  <div className="aspect-[4/3] overflow-hidden bg-white/5">
                    <ProductImage src={r.images?.[0]} alt={r.name}
                                  className="group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{r.category}</div>
                    <div className="mt-1.5 text-sm font-medium leading-tight">{r.name}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="font-display text-base text-accent">₹{r.price.toLocaleString("en-IN")}</div>
                      <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
